import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, RotateCcw, Check } from "lucide-react";
import { toast } from "sonner";
import { QUIZ_QUESTIONS, ARCHETYPES, scoreQuiz } from "@/data/content";
import { useCatalog } from "@/hooks/useCatalog";
import { money, img, postQuiz, postNewsletter } from "@/lib/api";

function ResultProducts({ slugs }) {
  const { bySlug, isLoading } = useCatalog();
  if (isLoading) return <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3"><div className="skeleton aspect-[3/4] rounded-2xl" /><div className="skeleton aspect-[3/4] rounded-2xl" /><div className="skeleton aspect-[3/4] rounded-2xl" /></div>;
  const products = slugs.map(bySlug).filter(Boolean);
  return (
    <div className="mt-8 grid grid-cols-2 gap-5 md:grid-cols-3">
      {products.map((p) => {
        const inStock = p.in_stock !== false;
        const priceLabel = p.price_min === p.price_max ? money(p.price_min) : `${money(p.price_min)} – ${money(p.price_max)}`;
        return (
          <Link key={p.id} to={`/product/${p.slug}`} data-testid={`quiz-rec-${p.slug}`} className="group flex flex-col">
            <div className="relative overflow-hidden rounded-t-[36%] rounded-b-2xl bg-sand">
              <img src={img(p.images[0], 500)} alt={p.name} className="aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              {!inStock && <span className="absolute right-2 top-2 rounded-full bg-rose px-2.5 py-1 text-[10px] font-semibold uppercase text-white">Unavailable</span>}
            </div>
            <h4 className="mt-3 font-serif text-lg leading-snug text-wood-900">{p.name}</h4>
            <span className="text-sm font-semibold text-wood-700">{priceLabel}</span>
          </Link>
        );
      })}
    </div>
  );
}

export default function Quiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => { document.title = "Self-Care Discovery Quiz · Washington Family Provisions"; }, []);

  const total = QUIZ_QUESTIONS.length;
  const progress = result ? 100 : Math.round((step / total) * 100);

  const choose = (qid, value) => {
    const next = { ...answers, [qid]: value };
    setAnswers(next);
    if (step + 1 < total) {
      setStep(step + 1);
    } else {
      const archetype = scoreQuiz(next);
      setResult(archetype);
      const categoryMap = { tea: "tea", honey: "honey", body: "body-care", set: "gift-sets" };
      postQuiz({
        archetype,
        need: next.need,
        experience: next.experience,
        sensory: next.sensory,
        audience: next.audience,
        category: categoryMap[next.experience] || null,
      }).catch(() => {});
    }
  };

  const retake = () => { setStep(0); setAnswers({}); setResult(null); setSubscribed(false); setEmail(""); };

  const subscribe = async (e) => {
    e.preventDefault();
    try { await postNewsletter({ email }); setSubscribed(true); toast.success("Thank you — your 15% code is on its way."); }
    catch { toast.error("Please enter a valid email."); }
  };

  const arch = result ? ARCHETYPES[result] : null;

  return (
    <div className="min-h-[80vh] bg-cream">
      <div className="mx-auto max-w-3xl px-5 py-14 md:px-8 md:py-20">
        {/* Progress */}
        <div className="mb-10">
          <div className="flex items-center justify-between text-sm text-wood-500">
            <span className="uppercase tracking-[0.2em]">{result ? "Your result" : `Question ${step + 1} of ${total}`}</span>
            <span>{progress}%</span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-sand">
            <motion.div className="h-full bg-honey" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
          </div>
        </div>

        {!result ? (
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            >
              {step > 0 && (
                <button onClick={() => setStep(step - 1)} className="mb-6 inline-flex items-center gap-2 text-base text-wood-500 hover:text-honey-dark">
                  <ArrowLeft size={16} /> Back
                </button>
              )}
              <h1 className="font-serif text-4xl leading-tight text-wood-900 md:text-5xl">{QUIZ_QUESTIONS[step].q}</h1>
              <div className="mt-8 flex flex-col gap-3">
                {QUIZ_QUESTIONS[step].options.map((o) => (
                  <button
                    key={o.value}
                    onClick={() => choose(QUIZ_QUESTIONS[step].id, o.value)}
                    data-testid={`quiz-option-${o.value}`}
                    className="group flex items-center justify-between rounded-2xl border-2 border-wood-900/15 bg-white px-6 py-5 text-left text-lg text-wood-900 transition-all hover:border-honey hover:bg-honey/5"
                  >
                    {o.label}
                    <span className="h-5 w-5 rounded-full border-2 border-wood-900/25 transition-colors group-hover:border-honey group-hover:bg-honey" />
                  </button>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="quiz-result">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-honey-dark">You are</p>
            <h1 className="mt-3 font-serif text-5xl text-wood-900 md:text-6xl">{arch.title}</h1>
            <p className="mt-4 max-w-xl text-lg leading-relaxed text-wood-700">{arch.blurb}</p>

            <h2 className="mt-12 font-serif text-2xl text-wood-900">Curated for you</h2>
            <ResultProducts slugs={arch.slugs} />

            <div className="mt-12 flex flex-wrap gap-3">
              <Link to="/shop/honey" className="rounded-full bg-wood-900 px-8 py-4 text-base font-medium text-cream transition-colors hover:bg-honey-dark">Explore the shop</Link>
              <button onClick={retake} data-testid="quiz-retake" className="inline-flex items-center gap-2 rounded-full border border-wood-900/30 px-8 py-4 text-base font-medium text-wood-900 transition-colors hover:bg-wood-900 hover:text-cream">
                <RotateCcw size={16} /> Retake quiz
              </button>
            </div>

            {/* Optional email — not required to see results */}
            <div className="mt-12 rounded-3xl bg-rose-light/80 p-7">
              <h3 className="font-serif text-2xl text-wood-900">Want 15% off your first order?</h3>
              <p className="mt-1 text-base text-wood-700">Optional — join our email list for a code on your first order of $20+.</p>
              <form onSubmit={subscribe} className="mt-4 flex flex-col gap-3 sm:flex-row">
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" data-testid="quiz-email" className="w-full rounded-full border border-wood-900/20 bg-cream px-6 py-4 text-wood-900 outline-none focus:border-honey-dark" />
                <button type="submit" className="whitespace-nowrap rounded-full bg-wood-900 px-8 py-4 font-medium text-cream transition-colors hover:bg-honey-dark">
                  {subscribed ? <span className="inline-flex items-center gap-2"><Check size={16} /> Done</span> : "Get my code"}
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
